/**
 * ProductReviews Component - Figma Design Implementation
 *
 * Displays product reviews matching the exact Figma specifications:
 * - Individual review cards with proper spacing and layout
 * - Star ratings with #FFBF01 color
 * - Customer name with "Verified Member" badge
 * - Review date formatting
 * - Share and helpful buttons
 * - Proper typography and spacing per Figma
 */

import React, { useState, useEffect } from 'react';
import { Link } from '@remix-run/react';
import { useReviewCache, type Review } from '~/context/review-context';
import { useUserReview } from '~/hooks/use-user-review';

interface ProductReviewsProps {
  productId: string;
  onWriteReviewClick: () => void;
  isLoggedIn: boolean;
  refreshKey?: number; // Increment this to trigger a refetch of reviews
  // Review status from parent (single source of truth)
  hasReviewed?: boolean;
  reviewStatus?: 'published' | 'pending' | 'rejected' | null;
  isLoadingUserReview?: boolean;
}

/**
 * Helper function to get a cookie by name
 */
function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

/**
 * Extract first name from raw_app_meta_data JSON string
 */
function extractFirstName(rawAppMetaData: string | null | undefined): string | null {
  if (!rawAppMetaData) return null;

  try {
    const parsed = JSON.parse(rawAppMetaData);
    return parsed?.first_name || null;
  } catch (error) {
    console.error('Error parsing raw_app_meta_data:', error);
    return null;
  }
}

/**
 * Format review date to MM/DD/YYYY format per Figma
 */
function formatReviewDate(dateString: string): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
}

/**
 * Star Rating Component - Using gold-leaf.svg icons
 * Matches the star ratings used elsewhere in the product page
 */
function StarRating({ rating }: { rating: number }) {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

  return (
    <div className="flex h-[18px] min-w-0 flex-shrink flex-row items-center gap-1 sm:max-w-[360px]">
      {/* Full stars */}
      {[...Array(fullStars)].map((_, i) => (
        <img
          key={`full-${i}`}
          src="/icons/gold-leaf.svg"
          alt=""
          className="h-[18px] w-[18px] flex-none"
        />
      ))}

      {/* Half star */}
      {hasHalfStar && (
        <img src="/icons/gold-leaf-half.svg" alt="" className="h-[18px] w-[18px] flex-none" />
      )}

      {/* Empty stars */}
      {[...Array(emptyStars)].map((_, i) => (
        <img
          key={`empty-${i}`}
          src="/icons/gold-leaf.svg"
          alt=""
          className="h-[18px] w-[18px] flex-none opacity-30"
        />
      ))}
    </div>
  );
}

/**
 * Verified Shield Icon Component
 */
function VerifiedShieldIcon() {
  return (
    <div className="relative h-[16px] w-[13px] flex-none">
      <svg width="13" height="16" viewBox="0 0 13 16" fill="none">
        <path d="M6.5 0L0 3V8C0 12.5 6.5 16 6.5 16S13 12.5 13 8V3L6.5 0Z" fill="#065F46" />
        <path d="M4 8L6 10L9 6" stroke="white" strokeWidth="1.5" fill="none" />
      </svg>
    </div>
  );
}

/**
 * Individual Review Card - Exact Figma Implementation
 */
function ReviewCard({ review }: { review: Review }) {
  // Try raw_app_meta_data first (legacy per-store reviews), then fall back to email prefix
  const metaName = extractFirstName(review.user?.raw_app_meta_data);
  let firstName = 'Anonymous';
  if (metaName) {
    firstName = metaName;
  } else if (review.user?.email) {
    const emailPrefix = review.user.email.split('@')[0];
    firstName = emailPrefix.charAt(0).toUpperCase() + emailPrefix.slice(1);
  }
  const reviewDate = formatReviewDate(review.created);

  // Handle sharing review - matches product modal share pattern
  const handleShareReview = async () => {
    // Get current URL - should be /product/{slug}
    const currentPath = window.location.pathname;
    const shareUrl = `${window.location.origin}${currentPath}#review-${review.id}`;

    // Create share text with review preview
    const reviewPreview =
      review.comment.length > 100 ? `${review.comment.substring(0, 100)}...` : review.comment;
    const shareText = `Check out this ${review.rating}-star review by ${firstName}: "${reviewPreview}"`;

    // Check if Web Share API is supported and can share URLs (same as product modal)
    if (navigator.share && navigator.canShare && navigator.canShare({ url: shareUrl })) {
      try {
        await navigator.share({
          title: `${review.rating}-Star Review`,
          text: shareText,
          url: shareUrl,
        });
        return; // Success, exit early
      } catch (error) {
        // User cancelled or share failed, fall back to clipboard
        console.error('Native share failed:', error);
      }
    }

    // Fallback: Copy to clipboard with better error handling (same as product modal)
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        // Could add a toast notification here similar to product modal
        console.log('Review link copied to clipboard');
      } else {
        // Fallback for older browsers - create temporary input
        const textArea = document.createElement('textarea');
        textArea.value = shareUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          const success = document.execCommand('copy');
          if (success) {
            console.log('Review link copied to clipboard');
          }
        } catch (err) {
          console.error('Fallback copy failed:', err);
        } finally {
          document.body.removeChild(textArea);
        }
      }
    } catch (error) {
      console.error('Clipboard copy failed:', error);
    }
  };

  return (
    <div
      id={`review-${review.id}`}
      className="flex h-auto w-full max-w-[466px] flex-col items-start gap-4 p-0"
    >
      {/* Review Copy Container */}
      <div className="flex h-auto w-full max-w-[426px] flex-col items-start gap-2 p-0">
        {/* Star Rating & Date */}
        <div className="flex h-[18px] w-full max-w-[426px] min-w-0 flex-row items-center justify-between gap-2 p-0">
          <StarRating rating={review.rating} />

          {/* Date */}
          <div className="flex h-3 flex-shrink-0 flex-row items-center gap-1.5 p-0">
            <span className="font-helvetica-neue text-xs leading-[14px] font-normal whitespace-nowrap text-[#4D4D4D]">
              {reviewDate}
            </span>
          </div>
        </div>

        {/* Review Comment */}
        <p className="font-helvetica-neue h-auto w-full max-w-[426px] text-sm leading-[22px] font-light tracking-[0.02em] break-words text-black">
          {review.comment}
        </p>
      </div>

      {/* Customer Name */}
      <div className="flex h-4 w-full max-w-[426px] flex-row items-center gap-1.5 p-0">
        {/* Customer Name & Verified Member */}
        <div className="flex h-4 w-auto flex-row flex-wrap items-center gap-1.5 p-0">
          <span className="font-helvetica-neue text-base leading-[19px] font-normal text-black">
            {firstName}
          </span>
          <span className="font-helvetica-neue text-sm leading-[17px] font-normal text-[#065F46]">
            Verified Member
          </span>
          <VerifiedShieldIcon />
        </div>
      </div>

      {/* Review Footer */}
      <div className="flex w-full max-w-[426px] flex-row items-center gap-2 py-2">
        {/* Share this Review */}
        <button
          onClick={handleShareReview}
          type="button"
          className="relative flex flex-grow touch-manipulation flex-row items-center gap-1.5 before:absolute before:inset-0 before:min-h-[44px] before:min-w-[44px] before:content-[''] hover:opacity-70 focus:ring-2 focus:ring-[#4D4D4D] focus:ring-offset-2 focus:outline-none active:opacity-50"
          aria-label={`Share review by ${firstName}`}
        >
          <div className="relative z-10 h-2.5 w-2.5 flex-shrink-0">
            {/* Share icon - same as global product modal */}
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" className="h-2.5 w-2.5">
              <path
                d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"
                stroke="#808080"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <polyline
                points="16,6 12,2 8,6"
                stroke="#808080"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <line
                x1="12"
                y1="2"
                x2="12"
                y2="15"
                stroke="#808080"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <span className="font-helvetica-neue relative z-10 text-xs leading-[14px] font-normal text-[#4D4D4D]">
            Share this review
          </span>
        </button>

        {/* Was this helpful? */}
        {/* <div className="flex flex-grow flex-row items-center justify-end gap-3">
          <span className="font-helvetica-neue text-xs leading-[14px] font-normal text-[#4D4D4D]">
            Was this helpful?
          </span> */}

        {/* Thumbs Up */}
        {/* <button
            className="relative flex flex-row items-center gap-1.5 before:absolute before:inset-0 before:min-h-[44px] before:min-w-[44px] before:content-[''] hover:opacity-70 focus:ring-2 focus:ring-[#4D4D4D] focus:ring-offset-2 focus:outline-none"
            aria-label="Mark review as helpful"
          >
            <img
              src="/locations/thumbs-up.svg"
              alt=""
              className="relative z-10 h-2.5 w-2.5 flex-shrink-0"
            />
            <span className="font-ibm-plex-sans relative z-10 text-xs leading-[14px] font-normal text-[#4D4D4D]">
              0
            </span>
          </button> */}

        {/* Thumbs Down */}
        {/* <button
            className="relative flex flex-row items-center gap-1.5 before:absolute before:inset-0 before:min-h-[44px] before:min-w-[44px] before:content-[''] hover:opacity-70 focus:ring-2 focus:ring-[#4D4D4D] focus:ring-offset-2 focus:outline-none"
            aria-label="Mark review as not helpful"
          >
            <img
              src="/locations/thumbs-down.svg"
              alt=""
              className="relative z-10 h-2.5 w-2.5 flex-shrink-0"
            />
            <span className="font-ibm-plex-sans relative z-10 text-xs leading-[14px] font-normal text-[#4D4D4D]">
              0
            </span>
          </button>
        </div> */}
      </div>

      {/* Separator Line */}
      <div className="h-px w-full max-w-[426px] bg-[#D9D9D9]"></div>
    </div>
  );
}

export default function ProductReviews({
  productId,
  onWriteReviewClick,
  isLoggedIn,
  refreshKey = 0,
  hasReviewed: hasReviewedProp,
  reviewStatus: reviewStatusProp,
  isLoadingUserReview: isLoadingUserReviewProp,
}: ProductReviewsProps) {
  const { getReviews, isLoadingReviews, fetchReviews } = useReviewCache();

  // Only call hook if props are not provided (backward compatibility)
  // This avoids duplicate hook instances when parent already calls useUserReview
  const shouldUseHook = hasReviewedProp === undefined || isLoadingUserReviewProp === undefined;
  const hookResult = useUserReview(shouldUseHook ? productId : undefined);
  
  const hasReviewed = hasReviewedProp !== undefined ? hasReviewedProp : hookResult.hasReviewed;
  const reviewStatus = reviewStatusProp !== undefined ? reviewStatusProp : hookResult.status;
  const isLoadingUserReview =
    isLoadingUserReviewProp !== undefined ? isLoadingUserReviewProp : hookResult.isLoading;
  const [isExpanded, setIsExpanded] = useState(false);
  const INITIAL_REVIEWS_COUNT = 3;

  // Track if component is mounted to avoid hydration issues
  const [isMounted, setIsMounted] = useState(false);

  // Mark as mounted after hydration
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Fetch reviews client-side using ReviewProvider (only after hydration with delay)
  useEffect(() => {
    if (isMounted && productId) {
      const cachedReviews = getReviews(productId);
      if (!cachedReviews || refreshKey > 0) {
        // Small delay to ensure hydration completes
        // If refreshKey > 0, force refetch even if cached (for review submission refresh)
        const timeoutId = setTimeout(() => {
          fetchReviews(productId);
        }, 100);
        return () => clearTimeout(timeoutId);
      }
    }
  }, [isMounted, productId, refreshKey, getReviews, fetchReviews]);

  // Refetch user review when refreshKey changes (after review submission)
  // Only refetch if we're using the hook (not props)
  useEffect(() => {
    if (refreshKey > 0 && hasReviewedProp === undefined) {
      hookResult.refetch();
    }
  }, [refreshKey, hasReviewedProp, hookResult]);

  const reviews = getReviews(productId) || [];
  const isLoadingReviewData = isLoadingReviews(productId);
  const hasMoreReviews = reviews.length > INITIAL_REVIEWS_COUNT;
  const displayedReviews = isExpanded ? reviews : reviews.slice(0, INITIAL_REVIEWS_COUNT);

  // Show loading state while fetching reviews
  if (isLoadingReviewData) {
    return (
      <div className="w-full">
        <div className="mb-4 flex flex-col gap-2">
          <h2 className="font-helvetica-neue text-xl text-black">Customer Reviews</h2>
        </div>
        <p className="font-helvetica-neue text-[#94A3B8]">Loading reviews...</p>
      </div>
    );
  }

  // Show empty state if no reviews
  if (reviews.length === 0) {
    return (
      <div className="w-full">
        <div className="mb-4 flex flex-col gap-2">
          <h2 className="font-helvetica-neue text-xl font-medium text-black">Customer Reviews</h2>
        </div>
        <p className="font-helvetica-neue text-[#334155]">
          {isLoadingUserReview ? (
            <>
              No reviews yet. Checking review status...
            </>
          ) : isLoggedIn && !hasReviewed ? (
            <>
              No reviews yet. Be the first to{' '}
              <button
                type="button"
                onClick={() => {
                  try {
                    onWriteReviewClick();
                  } catch (error) {
                    console.error('Error opening review modal:', error);
                  }
                }}
                className="font-helvetica-neue text-[#264B9A] underline transition-colors hover:text-[#1e3a7a] focus:ring-2 focus:ring-[#ffd100] focus:ring-offset-2 focus:outline-none"
                aria-label="Leave a review"
              >
                leave a review
              </button>
              .
            </>
          ) : isLoggedIn && hasReviewed ? (
            <>
              <span className="font-helvetica-neue text-[#334155]">
                No reviews yet.{' '}
                {reviewStatus === 'pending' ? (
                  <span className="font-medium text-[#065F46]">
                    Thank You! Review pending approval
                  </span>
                ) : (
                  <span className="font-medium text-[#065F46]">Thank you for your review!</span>
                )}
              </span>
            </>
          ) : (
            <>
              No reviews yet.{' '}
              <button
                type="button"
                onClick={() => {
                  window.dispatchEvent(new CustomEvent('login:trigger'));
                }}
                className="font-helvetica-neue text-[#264B9A] underline transition-colors hover:text-[#1e3a7a] focus:ring-2 focus:ring-[#ffd100] focus:ring-offset-2 focus:outline-none"
                aria-label="Open login dialog"
              >
                Login
              </button>{' '}
              or{' '}
              <Link
                to="/register"
                className="font-helvetica-neue text-[#264B9A] underline transition-colors hover:text-[#1e3a7a] focus:ring-2 focus:ring-[#ffd100] focus:ring-offset-2 focus:outline-none"
              >
                join now
              </Link>{' '}
              to leave a review.
            </>
          )}
        </p>
      </div>
    );
  }

  return (
    <div id="product-reviews" className="w-full">
      <div className="mb-4 flex flex-col gap-2">
        <h2 className="font-helvetica-neue text-xl font-medium text-black">Customer Reviews</h2>

        {/* Show review status or Leave a Review button */}
        {isLoadingUserReview ? (
          null
        ) : isLoggedIn && hasReviewed && reviewStatus !== 'rejected' ? (
          <div className="font-helvetica-neue text-base leading-5 text-[#334155]">
            {reviewStatus === 'pending' ? (
              <span className="font-medium text-[#065F46]">Thank You! Review pending approval</span>
            ) : (
              <span className="font-medium text-[#065F46]">Thank you for your review!</span>
            )}
          </div>
        ) : isLoggedIn && onWriteReviewClick ? (
          <button
            type="button"
            onClick={() => {
              try {
                onWriteReviewClick();
              } catch (error) {
                console.error('Error opening review modal:', error);
              }
            }}
            className="font-helvetica-neue inline-flex w-fit items-center gap-2 text-base leading-5 font-medium text-[#264B9A] underline transition-colors hover:text-[#1e3a7a] focus:ring-2 focus:ring-[#ffd100] focus:ring-offset-2 focus:outline-none"
            aria-label="Leave a review"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
              />
            </svg>
            Leave a Review
          </button>
        ) : !isLoggedIn ? (
          <p className="font-helvetica-neue text-sm text-[#64748B]">
            <button
              type="button"
              onClick={() => {
                window.dispatchEvent(new CustomEvent('login:trigger'));
              }}
              className="font-helvetica-neue text-[#264B9A] underline transition-colors hover:text-[#1e3a7a] focus:ring-2 focus:ring-[#ffd100] focus:ring-offset-2 focus:outline-none"
              aria-label="Open login dialog"
            >
              Login
            </button>{' '}
            or{' '}
            <Link
              to="/register"
              className="font-helvetica-neue text-[#264B9A] underline transition-colors hover:text-[#1e3a7a] focus:ring-2 focus:ring-[#ffd100] focus:ring-offset-2 focus:outline-none"
            >
              join now
            </Link>{' '}
            to leave a review.
          </p>
        ) : null}
      </div>

      {/* Reviews List - Figma Layout */}
      <div className="flex flex-col gap-6">
        {displayedReviews.map((review) => (
          <ReviewCard key={review.id} review={review} />
        ))}
      </div>

      {/* Read More Reviews Accordion Button */}
      {hasMoreReviews && (
        <div className="flex h-7 w-full max-w-[466px] flex-row items-center justify-center gap-2 py-1">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="relative flex flex-row items-center justify-center gap-2 before:absolute before:inset-0 before:min-h-[44px] before:min-w-[44px] before:content-[''] hover:opacity-70 focus:ring-2 focus:ring-[#264B9A] focus:ring-offset-2 focus:outline-none"
            aria-label={isExpanded ? 'Show fewer reviews' : 'Read more reviews'}
            aria-expanded={isExpanded}
          >
            <span className="font-commissioner relative z-10 text-base leading-5 font-medium text-[#264B9A]">
              {isExpanded ? 'Show fewer reviews' : 'Read more reviews'}
            </span>
            <div className="relative z-10 h-3.5 w-3.5">
              {/* Chevron/Dropdown Icon */}
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
              >
                <path
                  d="M3.5 5.25L7 8.75L10.5 5.25"
                  stroke="#264B9A"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </button>
        </div>
      )}
    </div>
  );
}
